import type { BillingInfo } from '$lib/billing';

// Source unique de vérité pour les quotas IA côté front.
// Doit rester synchronisé avec backend/flask_api/quotas.py
//
// Quota GLOBAL : un non-Pro a droit à TRIAL_LIMIT générations IA AU TOTAL,
// toutes features confondues (fiches + journal IA + progressions + évals + bulletins).

export const TRIAL_LIMIT = 3;
export const TRIAL_DAYS = 14;

// Plan de classe : nombre de plans qu'un non-Pro peut ENREGISTRER (capture).
// Générer/visualiser reste libre ; seul l'enregistrement est compté.
export const FREE_PLAN_CAPTURES = 1;

// Coloriage magique : nombre de COMBINAISONS distinctes qu'un non-Pro peut
// générer. Quota DÉDIÉ et SÉPARÉ du quota global (jamais sommé dans
// totalGenerations). Regénérer une combinaison déjà faite est gratuit.
export const FREE_COLORIAGE_GENERATIONS = 2;

export const PAYWALL_TITLE = 'Limite gratuite atteinte';
export const PAYWALL_BODY =
	'Vous avez utilisé vos 3 générations IA gratuites. ' +
	'Passez à Pro pour générer sans limite. Le cahier journal reste accessible en saisie manuelle, sans limite.';
export const PAYWALL_CTA = 'Voir les offres';
// Tarif Pro en vigueur (en euros). Sert à la copie et au suivi de conversion ;
// les abonnés historiques paient le prix de LEUR abonnement, lu dans `billing`.
export const PRO_PRICE_EUROS = { monthly: 9, annual: 89 } as const;
// CTA principal des murs de quota : ouvre le checkout Stripe directement.
export const PAYWALL_CTA_PRO = 'Passer à Pro — 9 €/mois';
// Réassurance affichée sous le CTA. Le mur court-circuite /pricing : les
// garanties qui y étaient expliquées doivent voyager avec le bouton.
export const PAYWALL_REASSURANCE = 'Sans engagement, résiliable en un clic · Remboursé sous 14 jours';

export type SubscriptionShape = {
	subscription_status?: string | null;
	subscription_tier?: string | null;
	trial_ends_at?: string | null;
	sequence_generations_count?: number;
	ai_journal_entries_count?: number;
	progression_generations_count?: number;
	evaluation_generations_count?: number;
	bulletin_generations_count?: number;
	classroom_plan_captures?: number;
	coloriage_generations_count?: number;
	trial_limit?: number;
	// Miroir de l'abonnement vivant (statut, facture impayée, carte) : lu par
	// le bandeau de facturation et le menu du compte.
	billing?: BillingInfo | null;
} | null;

export function hasProSubscription(sub: SubscriptionShape): boolean {
	if (!sub) return false;
	const status = sub.subscription_status;
	const tier = sub.subscription_tier;
	// 'past_due' : paiement en relance, accès maintenu le temps des nouvelles
	// tentatives Stripe (miroir de quotas.py).
	return (status === 'active' || status === 'canceling' || status === 'past_due') && tier !== 'free';
}

// Somme de toutes les générations IA, toutes features confondues.
export function totalGenerations(sub: SubscriptionShape): number {
	if (!sub) return 0;
	return (
		(sub.sequence_generations_count ?? 0) +
		(sub.ai_journal_entries_count ?? 0) +
		(sub.progression_generations_count ?? 0) +
		(sub.evaluation_generations_count ?? 0) +
		(sub.bulletin_generations_count ?? 0)
	);
}

export function canGenerate(sub: SubscriptionShape): boolean {
	if (hasProSubscription(sub)) return true;
	return totalGenerations(sub) < TRIAL_LIMIT;
}

export function remaining(sub: SubscriptionShape): number {
	if (hasProSubscription(sub)) return Infinity;
	return Math.max(0, TRIAL_LIMIT - totalGenerations(sub));
}

// Fiches d'exercices : quota DÉDIÉ, hors du quota global. Le compteur peut être
// négatif (fiches offertes en plus, geste de service du 18/09/2026) : -2 = 4 offertes.
export function coloriageQuota(sub: SubscriptionShape): { used: number; offered: number; remaining: number } {
	const count = sub?.coloriage_generations_count ?? 0;
	const offered = FREE_COLORIAGE_GENERATIONS - Math.min(0, count);
	const used = Math.max(0, count);
	return { used, offered, remaining: Math.max(0, offered - used) };
}

// Plan de classe : peut-on encore ENREGISTRER un plan (capture) ?
export function canCapturePlan(sub: SubscriptionShape, captures: number): boolean {
	if (hasProSubscription(sub)) return true;
	return captures < FREE_PLAN_CAPTURES;
}
